import {
  collection,
  doc,
  getDocs,
  query,
  orderBy,
  limit,
  setDoc,
  getDoc,
  deleteDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../../connection";
import { toast } from "react-toastify";
import logger from "../../../../../utils/logger";
import axios from "axios";

const uploadGiveawayItemImage = (image, id, ref) => {
  let path = `/giveaways/${id}/${ref}/itemImage`;
  return uploadImage(image, path);
};

const uploadDonatedByImage = (image, id, ref) => {
  let path = `/giveaways/${id}/${ref}/donatedByImage`;
  return uploadImage(image, path);
};

async function uploadImage(image, path) {
  let storageRef = ref(storage, path);
  const snapshot = await uploadBytes(storageRef, image);
  const url = await getDownloadURL(snapshot.ref);
  return url;
}

export const getLastGiveawayNumber = async (groupDocId) => {
  let id = "";

  try {
    const eventsCollectionRef = collection(
      db,
      "giveaways",
      groupDocId.trim(),
      "events",
    );
    const q = query(eventsCollectionRef, orderBy("addedOn", "desc"), limit(1));

    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      querySnapshot.forEach((document) => {
        if (Object.keys(document.data()).length > 0) {
          const giveaway = document.data();
          if (giveaway.uniqueId) {
            id = giveaway.uniqueId;
          }
        }
      });
    }
  } catch (error) {
    logger.error("Error fetching giveaway Id", error);
  }

  return id;
};

export const saveGiveawayToFirebase = async (
  groupData,
  isLive,
  itemImage,
  donatedByImage,
  giveawayData,
  isParent = false,
) => {
  try {
    if (!groupData?.id) {
      throw new Error("Group ID is missing");
    }

    // Create a reference to the collection where we'll add the document
    let eventsCollectionRef;
    if (isParent) {
      eventsCollectionRef = collection(
        db,
        `parentGroups/${groupData?.id}/giveaways`,
      );
    } else {
      eventsCollectionRef = collection(db, `giveaways/${groupData?.id}/events`);
    }

    // Create a new document reference with an auto-generated id
    const newGiveawayRef = doc(eventsCollectionRef);
    const giveawayId = newGiveawayRef.id;

    // For clubadmin, use clubId for image paths; for admin, use groupId
    const idForImagePath = isParent ? `parent_${groupData?.id}` : groupData?.id;

    let imageUrl = null;
    if (itemImage) {
      imageUrl = await uploadGiveawayItemImage(
        itemImage,
        idForImagePath,
        newGiveawayRef.id,
      );
    }

    let donatedByImageUrl = null;
    if (donatedByImage) {
      donatedByImageUrl = await uploadDonatedByImage(
        donatedByImage,
        idForImagePath,
        newGiveawayRef.id,
      );
    }

    // const imageUrl = "";
    // const donatedByImageUrl = "";

    const timestamp = Math.floor(Date.now() / 1000);

    // For clubadmin, use clubId for unique ID generation; for admin, use groupId
    const idForUniqueNumber = isParent
      ? `parent_${groupData?.id}`
      : groupData?.id;
    const temp = await getLastGiveawayNumber(idForUniqueNumber);
    let uniqueId = "G-1";
    if (temp.trim() != "") {
      const parts = temp.trim().split("-");
      const id = parseInt(parts[parts.length - 1]);
      uniqueId = `G-${id + 1}`;
    }

    // Format data for Firebase
    const formattedData = {
      allowFans: giveawayData?.allowFans || false,
      itemName: giveawayData?.itemName || "",
      stripeEmailId: groupData?.paymentDetails?.stripeAccountId || "",
      eventDesc: giveawayData?.details || "",
      itemImage: imageUrl || "",
      donatedByImage: donatedByImageUrl || "",
      donatedByText: giveawayData?.donatedByText || "",
      offerPurchase: giveawayData?.offerPurchase || false,
      goalAmount: Number(giveawayData?.goalAmount) || 0,
      receivedAmount: 0,
      noOfParticipants: 0,
      moreThanOnePurchase: giveawayData?.allowMultiplePurchases || false,
      isCompleted: false,
      isLive: isLive,
      isDraft: !isLive,
      animationsStarted: false,
      isWarningDone: false,
      isEntriesClosed: false,
      quantity: giveawayData?.noOfWinners || 1,
      addedOn: timestamp,
      eventDate: timestamp,
      updatedOn: timestamp,
      proceedsSupportName: "",
      proceedsSupportImage: "",
      priceEntryOptions: giveawayData?.priceOptions || [],
      partnerId: giveawayData?.proceedsSupport || "",
      multipleWinnerSelection: giveawayData?.multipleWinnerSelection || false,
      closeDate: giveawayData?.closeDate
        ? Math.floor(giveawayData.closeDate.valueOf() / 1000)
        : null,
      mustBePresentToWin: giveawayData?.mustBePresentToWin || false,
      locations: giveawayData?.locations || [],
      uniqueId: uniqueId,
    };

    // Add audience and groups if provided (for clubadmin)
    if (giveawayData?.audience) {
      formattedData.audience = giveawayData.audience;
    }
    if (giveawayData?.groups) {
      formattedData.groups = giveawayData.groups;
    }

    // Add groupId for clubadmin (used for filtering)
    formattedData.groupId = groupData?.id;

    // Save to Firestore using the reference we created
    await setDoc(newGiveawayRef, formattedData);

    if (isLive && !isParent) {
      sendGiveawayNotification(
        groupData?.id,
        groupData?.groupName || "",
        giveawayData?.itemName || "",
        giveawayData?.allowFans || false,
      );
    }

    toast.success("Giveaway created successfully!");
    return giveawayId;
  } catch (error) {
    logger.error("Error creating giveaway:", error);
    toast.error("Failed to create giveaway. Please try again.");
    return null;
  }
};

const sendGiveawayNotification = async (
  groupId,
  groupName,
  giveawayItemName,
  allowFans,
) => {
  try {
    const notificationBody = {
      type: "giveaway",
      gId: groupId,
      topic: `${groupId}_giveAwaysAlert`,
      tit: groupName + " giveaway",
      bdy: `A giveaway is now live. Enter to win: ${giveawayItemName}`,
      pId: "",
      sendFanNotification: allowFans,
    };

    await axios.post(
      `${import.meta.env.VITE_GCP_BASE_URL}/sendBulkNotification`,
      JSON.stringify(notificationBody),
      {
        headers: {
          "content-type": "application/json",
        },
      },
    );

    return true;
  } catch (error) {
    logger.error("Error sending giveaway notification:", error);
  }
};

export const updateGiveawayOnFirebase = async (
  groupData,
  isLive,
  giveawayId,
  itemImage,
  donatedByImage,
  giveawayData,
  isParent = false,
) => {
  try {
    if (!groupData?.id) {
      throw new Error("Group ID is missing");
    }

    if (!giveawayId) {
      throw new Error("Giveaway ID is missing");
    }

    // Get reference to the existing giveaway document
    let giveawayRef;
    if (isParent) {
      giveawayRef = doc(
        db,
        `parentGroups/${groupData?.id}/giveaways/${giveawayId}`,
      );
    } else {
      giveawayRef = doc(db, `/giveaways/${groupData?.id}/events/${giveawayId}`);
    }

    // For clubadmin, use clubId for image paths; for admin, use groupId
    const idForImagePath = isParent ? `parent_${groupData?.id}` : groupData?.id;

    let imageUrl = "";

    if (!itemImage) {
      imageUrl = "";
    } else if (typeof itemImage === "object") {
      imageUrl = await uploadGiveawayItemImage(
        itemImage,
        idForImagePath,
        giveawayId,
      );
    } else if (typeof itemImage === "string") {
      imageUrl = itemImage;
    }

    let donatedByImageUrl = "";
    if (!donatedByImage) {
      donatedByImageUrl = "";
    } else if (typeof donatedByImage === "object") {
      donatedByImageUrl = await uploadDonatedByImage(
        donatedByImage,
        idForImagePath,
        giveawayId,
      );
    } else if (typeof donatedByImage === "string") {
      donatedByImageUrl = donatedByImage;
    }

    const timestamp = Math.floor(Date.now() / 1000);

    // Format data for Firebase update
    const formattedData = {
      allowFans: giveawayData?.allowFans || false,
      itemName: giveawayData?.itemName || "",
      eventDesc: giveawayData?.details || "",
      itemImage: imageUrl || "",
      isLive: isLive,
      isDraft: !isLive,
      donatedByImage: donatedByImageUrl || "",
      donatedByText: giveawayData?.donatedByText || "",
      offerPurchase: giveawayData?.offerPurchase || false,
      goalAmount: Number(giveawayData?.goalAmount) || 0,
      moreThanOnePurchase: giveawayData?.allowMultiplePurchases || false,
      quantity: giveawayData?.noOfWinners || 1,
      updatedOn: timestamp,
      priceEntryOptions: giveawayData?.priceOptions || [],
      partnerId: giveawayData?.proceedsSupport || "",
      multipleWinnerSelection: giveawayData?.multipleWinnerSelection || false,
      closeDate: giveawayData?.closeDate
        ? Math.floor(giveawayData.closeDate.valueOf() / 1000)
        : null,
      mustBePresentToWin: giveawayData?.mustBePresentToWin || false,
      locations: giveawayData?.locations || [],
    };

    // Add audience and groups if provided (for clubadmin)
    if (giveawayData?.audience) {
      formattedData.audience = giveawayData.audience;
    }
    if (giveawayData?.groups) {
      formattedData.groups = giveawayData.groups;
    }

    // Add groupId for clubadmin (used for filtering)
    formattedData.groupId = groupData?.id;

    // Update the existing document
    await setDoc(giveawayRef, formattedData, { merge: true });

    if (isLive && !isParent) {
      sendGiveawayNotification(
        groupData?.id,
        groupData?.groupName || "",
        giveawayData?.itemName || "",
        giveawayData?.allowFans || false,
      );
    }

    toast.success("Giveaway updated successfully!");
    return giveawayId;
  } catch (error) {
    logger.error("Error updating giveaway:", error);
    toast.error("Failed to update giveaway. Please try again.");
    return null;
  }
};

export const fbGetGiveaway = async (groupId, giveawayId, isParent = false) => {
  try {
    let giveawayRef;
    if (isParent) {
      giveawayRef = doc(db, `parentGroups/${groupId}/giveaways/${giveawayId}`);
    } else {
      giveawayRef = doc(db, `/giveaways/${groupId}/events/${giveawayId}`);
    }
    const giveaway = await getDoc(giveawayRef);
    return giveaway.data();
  } catch (error) {
    logger.error("Error fetching giveaway:", error);
    return null;
  }
};

export const deleteAdminGiveaway = async (giveawayId, groupId) => {
  try {
    if (
      !giveawayId ||
      typeof giveawayId !== "string" ||
      giveawayId.trim() === ""
    ) {
      throw new Error("Invalid giveaway ID provided");
    }

    if (!groupId || typeof groupId !== "string" || groupId.trim() === "") {
      throw new Error("Invalid group ID provided");
    }

    const giveawayRef = doc(db, `/giveaways/${groupId}/events/${giveawayId}`);

    const giveawayDoc = await getDoc(giveawayRef);

    if (!giveawayDoc.exists()) {
      throw new Error("Giveaway not found");
    }

    const data = giveawayDoc.data();

    if (data?.receivedAmount > 0) {
      throw new Error("Cannot delete: payments have been collected.");
    }

    await deleteDoc(giveawayRef);
  } catch (error) {
    logger.error("Error deleting giveaway:", error);
    throw error;
  }
};
